import { useState } from "react";
import { Link } from "@inertiajs/react";
import { X } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@components/ui/dialog";
import { useCreditStore } from "~/stores/creditStore";
import { ConfirmUpgradeModal } from "@components/settings/ConfirmUpgradeModal";
import { useCreditModalVariant, type CreditModalKey } from "./creditModalVariant";

/**
 * Unified credit modal for trial-exhausted, plan-exhausted, and low-credit
 * states. All copy/palette/reset-label decisions live in
 * `useCreditModalVariant`; this component only handles layout and which
 * action block to render.
 */
export function CreditWarningModal() {
  const variant = useCreditModalVariant();
  const [confirmUpgradeOpen, setConfirmUpgradeOpen] = useState(false);

  return (
    <>
      {variant && (
        <CreditWarningDialog
          variant={variant}
          onUpgradeClick={() => {
            variant.dismiss();
            setConfirmUpgradeOpen(true);
          }}
        />
      )}

      <TrialUpgradeConfirm open={confirmUpgradeOpen} onOpenChange={setConfirmUpgradeOpen} />
    </>
  );
}

function CreditWarningDialog({
  variant,
  onUpgradeClick,
}: {
  variant: NonNullable<ReturnType<typeof useCreditModalVariant>>;
  onUpgradeClick: () => void;
}) {
  const { title, description, palette, resetLabel, progressPercent, balanceDisplay, dismiss } = variant;

  return (
    <Dialog open onOpenChange={(open) => !open && dismiss()}>
      <DialogContent
        className="sm:max-w-[600px] p-0 gap-0 [&>button]:hidden"
        data-testid="credit-modal"
      >
        <div className="px-12 pt-8 pb-8">
          <button
            type="button"
            onClick={dismiss}
            className="absolute right-12 top-9 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
            aria-label="Close"
            data-testid="credit-modal-close"
          >
            <X className="h-[18px] w-[18px]" />
          </button>

          <DialogTitle
            className="text-lg font-semibold text-[#0f1113] mb-0.5"
            data-testid="credit-modal-title"
          >
            {title}
          </DialogTitle>
          <DialogDescription className="text-sm text-[#74767a] max-w-[460px]">
            {description}
          </DialogDescription>

          <div className="flex justify-center my-6">
            <div
              className={`h-[60px] w-[60px] rounded-full ${palette.circleBg} flex items-center justify-center`}
            >
              <WarningIcon fill={palette.iconFill} />
            </div>
          </div>

          <div className="mb-8">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-sm font-semibold text-[#0f1113]">Credits Remaining</span>
              <span className="text-xs text-[#2e3238]" data-testid="credit-modal-balance">
                {balanceDisplay}
              </span>
            </div>
            <div
              className="h-[6px] w-full rounded-full bg-[#ededec] overflow-hidden"
              data-testid="credit-modal-progress"
            >
              <div
                className={`h-full rounded-full transition-all ${palette.barGradient}`}
                style={{ width: `${Math.min(progressPercent, 100)}%` }}
              />
            </div>
            {resetLabel && (
              <p className="text-xs text-[#96989b] mt-1.5">{resetLabel}</p>
            )}
          </div>

          <div className="flex items-center justify-end gap-4">
            <ActionsFor
              variant={variant.key}
              onUpgradeClick={onUpgradeClick}
              onDismiss={dismiss}
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ----------------------------------------------------------------------------

function WarningIcon({ fill }: { fill: string }) {
  return (
    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M10.9677 3.50488C12.3146 1.17025 15.6841 1.17025 17.031 3.50488L25.611 18.377C26.9572 20.7103 25.2732 23.626 22.5794 23.626H5.41928C2.72548 23.626 1.04148 20.7103 2.38763 18.377L10.9677 3.50488ZM13.9996 9.62592C14.4828 9.62592 14.8746 10.0177 14.8746 10.5009V14.8759C14.8746 15.3592 14.4828 15.7509 13.9996 15.7509C13.5163 15.7509 13.1246 15.3592 13.1246 14.8759V10.5009C13.1246 10.0177 13.5163 9.62592 13.9996 9.62592ZM13.9996 19.2509C14.4828 19.2509 14.8746 18.8592 14.8746 18.3759C14.8746 17.8927 14.4828 17.5009 13.9996 17.5009C13.5163 17.5009 13.1246 17.8927 13.1246 18.3759C13.1246 18.8592 13.5163 19.2509 13.9996 19.2509Z"
        fill={fill}
      />
    </svg>
  );
}

const PRIMARY_BUTTON =
  "text-base text-white bg-[#2e3238] border border-[#2e3238] rounded-lg py-3 px-3 hover:opacity-90 transition-opacity";
const SECONDARY_LINK = "text-base text-[#2e3238] hover:opacity-80 transition-opacity py-3 px-3";

function ActionsFor({
  variant,
  onUpgradeClick,
  onDismiss,
}: {
  variant: CreditModalKey;
  onUpgradeClick: () => void;
  onDismiss: () => void;
}) {
  const trialUpgrade = useCreditStore((s) => s.trialUpgrade);

  if (variant === "trial-exhausted") {
    // Inline upgrade via Stripe if we have the data, otherwise fall back to
    // the settings page where the user can pick a plan.
    if (trialUpgrade) {
      return (
        <button type="button" onClick={onUpgradeClick} className={PRIMARY_BUTTON}>
          Upgrade Now
        </button>
      );
    }
    return (
      <Link href="/settings" onClick={onDismiss} className={PRIMARY_BUTTON}>
        Upgrade Now
      </Link>
    );
  }

  // plan-exhausted and low-credits share the same two CTAs.
  return (
    <>
      <Link href="/settings" onClick={onDismiss} className={SECONDARY_LINK}>
        Upgrade Plan
      </Link>
      <Link href="/settings" onClick={onDismiss} className={PRIMARY_BUTTON}>
        Purchase Credits
      </Link>
    </>
  );
}

function TrialUpgradeConfirm({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const trialUpgrade = useCreditStore((s) => s.trialUpgrade);
  if (!trialUpgrade) return null;

  return (
    <ConfirmUpgradeModal
      open={open}
      onOpenChange={onOpenChange}
      subscriptionPrefixId={trialUpgrade.subscriptionPrefixId}
      planDisplayName={trialUpgrade.planDisplayName}
      amountCents={trialUpgrade.amountCents}
      currency={trialUpgrade.currency}
      interval={trialUpgrade.interval}
      paymentFailed={trialUpgrade.paymentFailed}
      declineMessage={trialUpgrade.declineMessage}
      updatePaymentMethodPath={trialUpgrade.updatePaymentMethodPath}
      onError={(message) => {
        // No persistent surface here (unlike the settings page), so show
        // the decline as a toast and link the user to Stripe's portal.
        toast.error(message, {
          action: trialUpgrade.updatePaymentMethodPath
            ? {
                label: "Update payment",
                onClick: () => {
                  window.location.href = trialUpgrade.updatePaymentMethodPath!;
                },
              }
            : undefined,
        });
      }}
    />
  );
}
